import React from 'react';
import { FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import Tooltip from '@cpg-its/ads.common.tooltip';
import { DeclinedValue, NewHireCoverageState } from '../models/coverage-models';
import { NewHireProps } from '../../../../models/arc-epi-props';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { summaryStyles } from '../../../../../../global/constants/theme/styles/pages/annual-enrollment/summary-section';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import { CalculateVolumeRate, CalculatedTiers } from '../../models/new-hire-enrollment-models';
import { getRateByCodeAndTier, getNameAndRateByCodeGroupLifeLongTernm } from '../helpers/coverage-display-helpers';
import { tcsmColors } from '../../../../../../global/constants/theme/theme-constants/colors';
import { WhoWillEnrollCriterias, WhoWillEnrollOptions } from '../../models/who-will-enroll-models';
import { ParticipantEnrollmentSettings } from '../../../new-participant-enrollment/models/epi-props';


interface Props {
    formikProps: FormikProps<any>;
    plans: NewHireCoverageState;
    settings: NewHireProps;
    isParticipant?: boolean;
    dioApproverMedicalPlansDisplayed?: boolean;
    dioApproverDentalPlansDisplayed?: boolean;
    dioApproverShortTermPlansDisplayed?: boolean;
    dioApproverLongTermPlansDisplayed?: boolean;
    dioApproverUeiPlansDisplayed?: boolean;
}


export function AnnualEnrollmentCoverageReviewDisplay(props: Props) {
    const summaryClasses = summaryStyles();
    const { formikProps, plans, settings, isParticipant, dioApproverMedicalPlansDisplayed, dioApproverDentalPlansDisplayed, dioApproverShortTermPlansDisplayed, dioApproverLongTermPlansDisplayed, dioApproverUeiPlansDisplayed } = props;

    const displayTierMedical = useSelector<TcsmStates, CalculatedTiers>(state => state?.newHireEnrollmentState?.tierToDisplayMedical);
    const displayTierDental = useSelector<TcsmStates, CalculatedTiers>(state => state?.newHireEnrollmentState?.tierToDisplayDental);
    const groupLifeValuesArray = useSelector<TcsmStates, CalculateVolumeRate[]>(state => state?.newHireEnrollmentState?.calculatedVolumeRateValues);
    const enrollmentCriteria = useSelector<TcsmStates, WhoWillEnrollCriterias>(state => state?.newHireEnrollmentState?.whoWillEnrollCriteria);
    const participantSettings = useSelector<TcsmStates, ParticipantEnrollmentSettings>(state => state.epiProps?.participantEnrollmentSettingsState?.epiContent);
    const medicalPlan = formikProps?.values?.M;
    const dentalPlan = formikProps?.values?.D;
    const groupLifePlanValue = formikProps?.values?.G;
    const longternmPlanValue = formikProps?.values?.L;
    const shortTermPlanValue = formikProps?.values?.W;
    const ueiPlanValue = formikProps?.values?.U;


    const medicalAmount: number = getRateByCodeAndTier(plans?.eligible_plans?.medical?.plans, medicalPlan, displayTierMedical?.selected_tier);
    const dentalAmount: number = getRateByCodeAndTier(plans?.eligible_plans?.dental?.plans, dentalPlan, displayTierDental?.selected_tier);
    let glRateFromApi: any = 0;
    let glCustomVolume: any = 0;

    if (!!groupLifeValuesArray && (Object.keys(groupLifeValuesArray).length !== 0)) {
        glRateFromApi = groupLifeValuesArray[`volume${formikProps?.values?.G}`]?.rate;
        glCustomVolume = groupLifeValuesArray[`volume${formikProps?.values?.G}`]?.volume;
    }
    const medicalPlanInfo = getNameAndRateByCodeGroupLifeLongTernm(plans?.eligible_plans?.medical?.plans, medicalPlan);
    const dentalPlanInfo = getNameAndRateByCodeGroupLifeLongTernm(plans?.eligible_plans?.dental?.plans, dentalPlan);
    const groupLifePlanRateInfo = getNameAndRateByCodeGroupLifeLongTernm(plans?.eligible_plans?.group_life?.plans, groupLifePlanValue, glRateFromApi !== 0 ? glRateFromApi : 0, glCustomVolume !== 0 ? glCustomVolume : 0);
    const longTermPlanRateInfo = getNameAndRateByCodeGroupLifeLongTernm(plans?.eligible_plans?.long_term_disability?.plans, longternmPlanValue);
    const shortTermPlanRateInfo = getNameAndRateByCodeGroupLifeLongTernm(plans?.eligible_plans?.short_term_disability?.plans, shortTermPlanValue);
    const ueiPlanRateInfo = getNameAndRateByCodeGroupLifeLongTernm(plans?.eligible_plans?.uei?.plans, ueiPlanValue);

    const hasMedical = plans?.eligible_plans?.medical?.plans?.length > 0;
    const hasDental = plans?.eligible_plans?.dental?.plans?.length > 0;
    const hasGroupLife = plans?.eligible_plans?.group_life?.plans?.length > 0;
    const hasLtd = plans?.eligible_plans?.long_term_disability?.plans?.length > 0;
    const hasStd = plans?.eligible_plans?.short_term_disability?.plans?.length > 0;
    const hasUei = plans?.eligible_plans?.uei?.plans?.length > 0;

    function getPlanDisplay(amount: number, declineMessage: string, planName: string, enrollmentOption: WhoWillEnrollOptions, pushToMember: boolean, planTitle: string, isDioApproverPlansDisplayed: boolean = false, isdeclined: boolean = false, hasError = false ) {

        if (hasError) {
            return (
                <div className='summary-row'>
                    <ContentSpacer>
                        <p>{settings?.invalidValueHasBeenSetMessage}</p>
                    </ContentSpacer>
                </div>
            );
        }

        if (enrollmentOption === WhoWillEnrollOptions.member && isdeclined !== true) {
            return (
                pushToMember ?
                    <div className='summary-row'>
                        <ContentSpacer>
                            <p><strong>{planTitle}</strong></p>
                            <WysiwygWithReactLink content={settings?.pendingMemberReviewMessage} />
                        </ContentSpacer>
                    </div>
                    : null
            );
        }

        if (!isDioApproverPlansDisplayed) {
            return (
                <div className='summary-row'>
                    <ContentSpacer>
                        <p><strong>{planTitle}</strong></p>
                        <WysiwygWithReactLink content={settings?.pendingMemberReviewMessage} />
                    </ContentSpacer>
                </div>
            );
        }



        return (
            <>
                {
                    amount !== 0 ? (
                        <div className='summary-row'>
                            <p>{planName}</p>
                            <p>{getCurrencyFormat(amount)}</p>
                        </div>
                    ) : (
                        <>
                                <ContentSpacer>
                                <div className='summary-decline'>
                                    <p>{declineMessage}</p>
                                </div>
                            </ContentSpacer>
                        </>
                    )
                }
            </>
        );
    }

    function renderMedicalAndDental() {

        return (
            <>
                {
                    (hasMedical || hasDental) && (
                        <>
                            <ContentSpacer>
                                <div className='summary-tooltip'>
                                    <h3>{settings?.monthlyCostLabel}</h3>
                                    <div className='summary-tooltip-inner'><Tooltip iconColor={tcsmColors.blue} title={isParticipant ? participantSettings?.participantMonthlyCostHelperText : settings.monthlyCostHelper} /></div>
                                </div>
                            </ContentSpacer>
                            <div className='summary-header'>
                                <div className='summary-tooltip'>
                                    <h4>{settings?.monthlyCostHealthLabel}</h4>
                                    <div className='summary-tooltip-inner'>
                                        <Tooltip iconColor={tcsmColors.blue} title={settings.monthlyCostHealthHelper} />
                                    </div>
                                </div>
                            </div>
                        </>
                    )
                }
                {
                    hasMedical &&
                    getPlanDisplay(
                        medicalAmount,
                        settings?.declineMedicalCoverageMessage,
                        medicalPlanInfo?.name,
                        enrollmentCriteria?.medical,
                        formikProps?.values?.M_member,
                        settings?.medicalPlanTitle,
                        dioApproverMedicalPlansDisplayed,
                        formikProps?.values?.M === DeclinedValue,
                        !!formikProps?.errors?.M,
                    )
                }
                {
                    hasDental &&
                    getPlanDisplay(
                        dentalAmount,
                        settings?.declineDentalCoverageMessage,
                        dentalPlanInfo?.name,
                        enrollmentCriteria?.dental,
                        formikProps?.values?.D_member,
                        settings?.dentalPlanTitle,
                        dioApproverDentalPlansDisplayed,
                        formikProps?.values?.D === DeclinedValue,
                        !!formikProps?.errors?.D,
                    )

                }

                {/* MEDICAL & DENTAL SUM */}
                {
                    (hasMedical || hasDental) && (
                        <>
                            <div className='summary-subtotal'>
                                <div className='summary-row'>
                                    <p><strong>{settings?.healthSubTotalLabel}</strong></p>
                                    <p>{getCurrencyFormat((medicalAmount + dentalAmount))}</p>
                                </div>
                            </div>
                        </>
                    )
                }
            </>
        );
    }

    function renderDisability() {
        return (
            <>
                {
                    (hasStd || hasLtd) && (
                        <div className='summary-header'>
                            <div className='summary-tooltip'>
                                <p className='h4-styled'>{settings.disabilityCostLabel}</p>
                                <div className='summary-tooltip-inner'>
                                    <Tooltip iconColor={tcsmColors.blue} title={settings.disabilityCostHelper} />
                                </div>
                            </div>
                        </div>
                    )
                }

                {
                    hasStd &&
                    getPlanDisplay(
                        shortTermPlanRateInfo?.rate,
                        settings?.declineShortDisPlanOptionText,
                        shortTermPlanRateInfo?.name,
                        enrollmentCriteria?.std ?? enrollmentCriteria?.disability,
                        formikProps?.values?.W_member,
                        settings?.shortDisPlanTitle,
                        dioApproverShortTermPlansDisplayed,
                        formikProps?.values?.W === DeclinedValue,
                        !!formikProps?.errors?.W,
                    )
                }
                {
                    hasLtd &&
                    getPlanDisplay(
                        longTermPlanRateInfo?.rate,
                        settings?.declineLongDisPlanOptionText,
                        longTermPlanRateInfo?.name,
                        enrollmentCriteria?.ltd,
                        formikProps?.values?.L_member,
                        settings?.longDisPlanTitle,
                        dioApproverLongTermPlansDisplayed,
                        formikProps?.values?.L === DeclinedValue,
                        !!formikProps?.errors?.L,
                    )
                }

                {
                    (!!shortTermPlanRateInfo?.rate || !!longTermPlanRateInfo?.rate) && (
                        <div className='summary-row'>

                            <p>{settings?.disabilitySubTotalLabel}</p>

                            <p>{getCurrencyFormat((shortTermPlanRateInfo?.rate + longTermPlanRateInfo?.rate))}</p>
                        </div>
                    )
                }
            </>
        );

    }

    function renderGroupLife() {
        return (hasGroupLife &&
            <>
                <div className='summary-header'>
                    <div className='summary-tooltip'>
                        <p className='h4-styled'>{settings.groupCostLabel}</p>
                        <div className='summary-tooltip-inner'>
                            <Tooltip iconColor={tcsmColors.blue} title={settings.groupCostHelper} />
                        </div>
                    </div>
                </div>
                {
                    groupLifePlanRateInfo?.name && (!groupLifePlanRateInfo?.volume || groupLifePlanRateInfo?.volume === 0) &&
                    <>
                        <ContentSpacer>
                            <WysiwygWithReactLink content={settings?.groupRateSummaryInstitutionAdminSectionLabel} />
                        </ContentSpacer>
                    </>
                }
                {
                    groupLifePlanRateInfo?.rate !== 0 ? (
                        <>
                            <div className='summary-row'>
                                <p>{groupLifePlanRateInfo?.name}</p>
                                <p>{getCurrencyFormat(groupLifePlanRateInfo?.rate)}</p>
                            </div>
                            <div className='summary-subtotal'>
                                <div className='summary-row'>
                                    <p>{settings?.groupCostSubTotalLabel}</p>
                                    <p className='h4-styled'>{getCurrencyFormat((groupLifePlanRateInfo?.rate))}</p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <ContentSpacer>
                                <div className='summary-decline'>
                                    <p>{settings?.declineGroupLifePlanOptionText}</p>
                                </div>
                            </ContentSpacer>
                        </>
                    )
                }

            </>
        );

    }

    function renderUei(planTitle: string) {
        if (!dioApproverUeiPlansDisplayed) {
            return (
                <div className='summary-row'>
                    <ContentSpacer>
                        <p><strong>{planTitle}</strong></p>
                        <WysiwygWithReactLink content={settings?.pendingMemberReviewMessage} />
                    </ContentSpacer>
                </div>
            );
        }

        return (
            <>
                <ContentSpacer>
                    <div className='summary-decline'>
                        <p>{settings?.declineUeiPlanOptionText}</p>
                    </div>
                </ContentSpacer>
            </>
        );
    }

    return (
        <>
            <DividerSection removeDivider>
                <div className={summaryClasses.summarySection}>
                    <div className='summary'>
                        <h2 className='summary-header'>{settings?.summarySectionTitle}</h2>
                        {/* MEDICAL & DENTAL SECTION */}
                        {
                            renderMedicalAndDental()
                        }
                        {/* GL SECTION */}
                        {
                            renderGroupLife()
                        }

                        {/* SHORT TERM & LONG TERM & UIE SECTION */}
                        {
                            renderDisability()
                        }
                        {/* UEI SECTION */}
                        {
                            hasUei && (

                                <div className='summary-header'>
                                    <div className='summary-tooltip'>
                                        <p className='h4-styled'>{settings.ueiCostLabel}</p>
                                        <div className='summary-tooltip-inner'>
                                            <Tooltip iconColor={tcsmColors.blue} title={settings.ueiCostHelper} />
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                        {
                            hasUei && (
                                <>
                                    {
                                        ueiPlanRateInfo?.rate !== 0 ? (
                                            <>
                                                <div className='summary-row'>
                                                    <p>{ueiPlanRateInfo?.name}</p>
                                                    <p>{getCurrencyFormat(ueiPlanRateInfo?.rate)}</p>
                                                </div>
                                                <div className='summary-subtotal'>
                                                    <div className='summary-row'>
                                                        <p>{settings?.ueiSubTotalLabel}</p>
                                                        <p>{getCurrencyFormat(ueiPlanRateInfo?.rate)}</p>
                                                    </div>
                                                </div>
                                            </>

                                        ) : (
                                            renderUei(ueiPlanRateInfo?.name)
                                        )
                                    }
                                </>
                            )
                        }

                        <div className='summary-total'>
                            <div className='summary-total-row'>
                                <p className='h3-styled'>{settings.totalCostLabel}</p>
                                <p className='h3-styled'>{getCurrencyFormat(dentalAmount + medicalAmount + groupLifePlanRateInfo?.rate + shortTermPlanRateInfo?.rate + longTermPlanRateInfo?.rate + ueiPlanRateInfo?.rate)}</p>
                            </div>
                        </div>
                        <p> {isParticipant ? participantSettings?.participantTotalCostDescriptionText : settings?.totalCostDescription} </p>

                    </div>
                </div>
            </DividerSection>
        </>
    );
}

AnnualEnrollmentCoverageReviewDisplay.defaultProps = {
    isParticipant: false,
    dioApproverMedicalPlansDisplayed: false,
    dioApproverDentalPlansDisplayed: false,
    dioApproverShortTermPlansDisplayed: false,
    dioApproverLongTermPlansDisplayed: false,
    dioApproverUeiPlansDisplayed: false,
};